import React, { useState } from 'react';
import { Project } from '../../types/project';
import { Listbox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';
import { Input } from '../ui/input';
import { motion } from 'framer-motion';

interface ProjectSelectorProps {
  projects: Project[];
  value?: string | null;
  selectedProject?: Project | null;
  onChange: (project: Project | null) => void;
  className?: string;
  placeholder?: string;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  projects,
  value,
  selectedProject: selectedProjectProp,
  onChange,
  className,
  placeholder = "Select a project (optional)"
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const selectedProject = selectedProjectProp || (value ? projects.find(p => p.id === value) || null : null);

  const filteredProjects = projects.filter(project => {
    if (!searchQuery.trim()) return true;
    
    const searchLower = searchQuery.toLowerCase();
    const projectName = project.name.toLowerCase();
    const projectDesc = project.description?.toLowerCase() || '';

    return projectName.includes(searchLower) || 
           projectDesc.includes(searchLower);
  });

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === 'Enter' && filteredProjects.length === 1) {
      onChange(filteredProjects[0]);
    }
  };

  return (
    <motion.div 
      className={cn("relative flex-1", className)}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Listbox 
        value={selectedProject}
        onChange={onChange}
      >
        {({ open }) => (
          <div className="relative">
            <Listbox.Button 
              className="relative w-full cursor-pointer rounded-lg bg-background py-2.5 pl-4 pr-10 text-left border border-input shadow-sm hover:border-primary/30 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
              role="combobox"
            >
              <div className="flex items-center gap-3 min-w-0">
                {selectedProject ? (
                  <>
                    <div 
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0 ring-2 ring-offset-2 ring-offset-background"
                      style={{ 
                        backgroundColor: selectedProject.color,
                        borderColor: selectedProject.color 
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {selectedProject.name}
                      </p>
                    </div>
                  </>
                ) : (
                  <span className="text-sm text-muted-foreground/80">{placeholder}</span>
                )}
              </div>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <ChevronUpDownIcon className="h-4 w-4 text-muted-foreground/70" />
              </span>
            </Listbox.Button>

            {open && (
              <Listbox.Options static className="absolute z-50 mt-2 w-full overflow-hidden rounded-lg bg-background shadow-lg ring-1 ring-black/5 focus:outline-none border border-border animate-in fade-in-0 zoom-in-95">
                <div className="sticky top-0 p-2 bg-background border-b border-border/50">
                  <div className="relative">
                    <Input
                      placeholder="Search projects..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      className="pl-9 bg-background transition-colors"
                    />
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
                  </div>
                </div>

                <div className="max-h-60 overflow-auto py-1 scrollbar-thin scrollbar-thumb-muted-foreground/10 hover:scrollbar-thumb-muted-foreground/20">
                  <Listbox.Option
                    value={null}
                    className={({ active }) =>
                      cn(
                        'relative cursor-pointer select-none py-2 pl-10 pr-4 transition-colors',
                        active ? 'bg-accent text-accent-foreground' : ''
                      )
                    }
                  >
                    {({ selected }) => (
                      <>
                        <span className={cn('block truncate text-sm', selected ? 'font-medium' : 'font-normal')}>
                          No Project
                        </span>
                        {selectedProject === null && (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                            <CheckIcon className="h-4 w-4" />
                          </span>
                        )}
                      </>
                    )}
                  </Listbox.Option>

                  {filteredProjects.map((project) => (
                    <Listbox.Option
                      key={project.id}
                      value={project}
                      className={({ active }) =>
                        cn(
                          'relative cursor-pointer select-none py-2 pl-10 pr-4 transition-colors',
                          active ? 'bg-accent text-accent-foreground' : ''
                        )
                    }
                    >
                      {({ selected }) => (
                        <>
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0 ring-2 ring-offset-2 ring-offset-background"
                              style={{ 
                                backgroundColor: project.color,
                                borderColor: project.color
                              }}
                            />
                            <div>
                              <p className={cn(
                                'text-sm leading-tight',
                                selected ? 'font-medium' : 'font-normal'
                              )}>
                                {project.name}
                              </p>
                              {project.description && (
                                <p className="text-xs text-muted-foreground/70 mt-0.5 line-clamp-1">
                                  {project.description}
                                </p>
                              )}
                            </div>
                          </div>
                          {selectedProject?.id === project.id && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                              <CheckIcon className="h-4 w-4" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                  ))}

                  {filteredProjects.length === 0 && (
                    <div className="py-8 px-4 text-sm text-muted-foreground/70 text-center">
                      No projects found
                    </div>
                  )}
                </div>
              </Listbox.Options>
            )}
          </div>
        )}
      </Listbox>
    </motion.div>
  );
};